import { useCallback, useReducer } from 'react'
import {
  AnyObject,
  InputEvent,
  OptionProps,
  TextAreaEvent
} from 'typings/custom'
import {
  isEmailField,
  isUserIDField,
  validate,
  validateNoWhitespace
} from 'helpers/fieldValidation'
import {
  formatFieldName,
  removeWhitespace,
  swapMaskValue
} from 'helpers/fieldFormatters'
import { UseFormState, UseFormType } from './typing'

const reducer = (
  state: AnyObject,
  { type, payload }: UseFormType
): AnyObject => {
  switch (type) {
    case 'onChange':
      return { ...state, [payload.name]: payload.value }
    case 'reset':
      return { ...payload.state }
    case 'update':
      return { ...state, ...payload }
    default:
      return state
  }
}

/*
 * NOTE ABOUT MASKING/UNMASKING VALUES
 *
 * Use the InputMask component to mask form input values when the input loses
 * focus instead of using the mask/unmask feature of the useFormState hook.
 * Eventually, masking/unmasking will be removed from this hook once all
 * components have been updated to use the InputMask component for the masking
 * capability. Once the useFormState hook has had the masking feature removed,
 * this comment should be removed.
 */

/**
 * Returns object containing field values and methods to interact with form
 * values and errors.
 *
 * The form state is returned with the following:
 *   - Value of each field where the key corresponds to the name attribute on
 *     the input element and contains the current value of the field.
 *   - Error message to indicate if there is an error. Follows naming pattern
 *     %field_name%Error. (e.g. emailError, passwordError, etc)
 *
 * Example:
 *
 * Given initial state:
 * {
 *   email: '',
 *   password: ''
 * }
 *
 * Then the returned state will be:
 * {
 *   email: '',
 *   emailError: '',
 *   password: '',
 *   passwordError: ''
 * }
 *
 * Usage with data-* attributes on input elements:
 *
 * data-name:
 *
 * Used to set the name of the field in error messages. If no data-name is
 * present, then the error message will fallback to using the name attribute.
 * Example name="Phone" data-name="Mobile Phone" will produce error message with
 * "Mobile Phone" in the error message template instead of "Phone".
 *
 * data-type:
 *
 * Used in validation and error message.
 *
 * data-minLength:
 *
 * Used in validation of a string input to enforce a minimum length.
 *
 * data-maxLength:
 *
 * Used in validation of a string input to enforce a maximum length.
 *
 * data-customMessage:
 *
 * Used in validation. When this attributed is defined on an input with
 * maxLength, minLength, or maxValue data-* attribute, the provided custom error
 * message is returned instead of the default invalid field message.
 *
 * data-value:
 *
 * Used in validation and error message.
 *
 * data-maxValue:
 *
 * Used in validation of numeric input. A number that exceeds maxValue is
 * considered invalid.
 */
const useFormState = (initialState: AnyObject): UseFormState => {
  const [state, dispatch] = useReducer(reducer, initialState)

  /*
   * This function is used within other hooks and needs a constant reference to
   * avoid causing unwanted re-render when used with effects. Hence, useCallback
   * is invoked. The dispatch method from Redux is assumed to be a constant
   * reference. Therefore, updateState should be instantiated only when mounted.
   */
  const updateState = useCallback(
    (payload: AnyObject): void => {
      dispatch({ type: 'update', payload })
    },
    [dispatch]
  )

  const checkIfRequiredFieldsEntered = (
    fieldsEntered: Set<string>
  ): boolean => {
    const requiredFieldsFromEnteredFields = Array.from(fieldsEntered).filter(
      field => state.requiredFields.includes(field)
    )
    return (
      JSON.stringify(requiredFieldsFromEnteredFields.sort()) ===
      JSON.stringify(state.requiredFields.sort())
    )
  }

  const isFieldEntered = (name: string, value: string): void => {
    const { requiredFields, totalFields, fieldsEntered = new Set() } = state
    if (value) {
      fieldsEntered.add(name)
    } else {
      fieldsEntered.delete(name)
    }

    updateState({
      fieldsEntered,
      isAllFieldEntered: fieldsEntered.size === totalFields,
      isRequiredFieldsEntered: requiredFields
        ? checkIfRequiredFieldsEntered(fieldsEntered)
        : false
    })
  }

  const resetIsFieldError = (name: string): void => {
    const { fieldsError = new Set() } = state
    if (fieldsError.size === 1 && fieldsError.has(name)) {
      fieldsError.delete(name)
      updateState({ fieldsError, isFieldError: false })
    }
  }

  const onChange = (event: InputEvent | TextAreaEvent): void => {
    const {
      target: { name, value }
    } = event
    let normalizedValue = value
    if (isUserIDField(name) || isEmailField(name)) {
      normalizedValue = removeWhitespace(value)
    }
    if (validateNoWhitespace(name, normalizedValue)) {
      isFieldEntered(name, normalizedValue)
      resetIsFieldError(name)
      dispatch({
        type: 'onChange',
        payload: { name, value: normalizedValue, [`${name}Dirty`]: false }
      })
    }
  }

  const selectOnChange = (name: string, selectedOption: OptionProps): void => {
    const { value = '' } = selectedOption
    isFieldEntered(name, value)
    updateState({ [name]: selectedOption })
  }

  const reset = (resetState: AnyObject): void => {
    dispatch({ type: 'reset', payload: resetState })
  }

  const validateField = (event: InputEvent): void => {
    const {
      target: {
        name,
        value,
        dataset: {
          type = '',
          minLength = '',
          maxLength = '',
          customMessage = '',
          value: maskedField = '',
          name: errorName = '',
          minValue = ''
        }
      }
    } = event

    const fieldName = `${name}Error`

    const validatedMessage = validate({
      name,
      value,
      type,
      minLength: parseInt(minLength, 10),
      maxLength: parseInt(maxLength, 10),
      customMessage,
      errorName,
      minValue,
      isRequiredField: state.requiredFields?.includes(name)
    })
    const { fieldsError = new Set(), resetErrorFields = {} } = state

    if (validatedMessage) {
      fieldsError.add(name)
    } else {
      fieldsError.delete(name)
    }

    updateState({
      [fieldName]: validatedMessage,
      resetErrorFields: {
        ...resetErrorFields,
        [fieldName]: false
      },
      fieldsError,
      isFieldError: fieldsError.size > 0,
      [`${name}Dirty`]: true
    })

    if (maskedField && !validatedMessage) {
      /*
       * why is there still code for swapping masked values when this was
       * thought to have been completely removed with InputMask.
       */
      updateState(swapMaskValue(event))
    }
  }

  const resetFieldError = (event: InputEvent): void => {
    const {
      target: {
        name,
        dataset: { value: maskedField = '' }
      }
    } = event
    const fieldName = `${name}Error`

    const fieldError = state[fieldName]

    updateState({ [fieldName]: '', [`${name}Dirty`]: false })
    if (maskedField && !fieldError) {
      // why is swapMaskValue still here?
      updateState(swapMaskValue(event))
    }
  }

  const compareFieldValue = ({
    target: {
      name,
      value,
      dataset: { prevValue = '' }
    }
  }: InputEvent): void => {
    const fieldName = `is${formatFieldName(name)}`
    const { resetCompareFields = {} } = state

    updateState({
      [fieldName]: value !== prevValue,

      resetCompareFields: {
        ...resetCompareFields,
        [fieldName]: false
      }
    })
  }

  const resetcompareFieldValue = (): void => {
    const { resetCompareFields = {} } = state
    updateState(resetCompareFields)
  }

  const resetFormState = (): void => {
    const { resetErrorFields = {}, resetCompareFields = {} } = state
    resetcompareFieldValue()
    updateState({
      ...initialState,
      ...resetErrorFields,
      ...resetCompareFields,
      fieldsEntered: new Set(),
      isAllFieldEntered: false,
      fieldsError: new Set(),
      isFieldError: false
    })
  }

  return {
    state,
    onChange,
    selectOnChange,
    reset,
    resetFormState,
    updateState,
    validateField,
    resetFieldError,
    compareFieldValue,
    resetcompareFieldValue
  }
}

export default useFormState
